import { useMutation } from '@tanstack/react-query';
import { queryClient } from 'src/components/queryProvider';
import { COLLECTIONS } from 'src/constants/constants';
import { ROTATION_SECTION_CONFIG } from 'src/constants/gcfSectionConfig';
import { ICanvasSection, ICreateSectionPayload } from 'src/interfaces';
import { queryKeys, requestCCApi } from 'src/utils';

import {
  getOrCreateSectionId,
  getOrCreateSectionTypeId,
} from './useCreateScreenSection';

const createRotationScreenSection = async (payload: ICreateSectionPayload) => {
  const screenID = payload.screen.id;
  const sectionCache = new Map<string, Promise<number>>();
  const sectionTypeID = await getOrCreateSectionTypeId(
    payload.layoutGridLine,
    payload.screen,
  );

  const getOrCacheSectionId = async (section: ICanvasSection) => {
    const sectionKey = `${section.width}x${section.height}x${section.rows}x${section.cols}`;
    if (!sectionCache.has(sectionKey)) {
      const sectionPromise = getOrCreateSectionId(section, sectionTypeID);
      sectionCache.set(sectionKey, sectionPromise);
    }

    return sectionCache.get(sectionKey);
  };

  const screenSectionsPayloadData = await Promise.all(
    payload.sections.map(async (section) => {
      const sectionID = await getOrCacheSectionId(section);

      return {
        resource: COLLECTIONS.SCREEN_SECTIONS,
        method: 'POST',
        data: {
          section_id: sectionID,
          left_offset: section.leftOffset,
          top_offset: section.topOffset,
          config: ROTATION_SECTION_CONFIG,
          screen_id: screenID,
        },
      };
    }),
  );

  await requestCCApi({
    url: '/transaction',
    method: 'POST',
    data: {
      data: screenSectionsPayloadData,
    },
  });
};

export const useCreateRotationScreenSection = () => {
  return useMutation({
    mutationFn: createRotationScreenSection,
    onSuccess: () => {
      queryClient.invalidateQueries(queryKeys.screenSections.invalidateAll());
      queryClient.invalidateQueries(queryKeys.layoutScreens.invalidateAll());
      queryClient.invalidateQueries(queryKeys.layouts.invalidateAll());
    },
  });
};
